import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
  Tooltip,
} from "recharts";
import { apiClient } from "../../../config/api";
import API_CONFIG from "../../../config/api";

const COLORS = {
  open: "#ff8912", // Orange
  inProgress: "#00d4ff", // Cyan
  closed: "#39ff14", // Green
};

const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="incident-severity-chart__tooltip">
        <p className="incident-severity-chart__tooltip-label">{label}</p>
        {payload.map((entry, index) => (
          <p
            key={index}
            className="incident-severity-chart__tooltip-item"
            style={{ color: entry.color }}
          >
            {entry.name}: {entry.value}
          </p>
        ))}
        <p className="incident-severity-chart__tooltip-total">
          Total: {payload.reduce((sum, entry) => sum + entry.value, 0)}
        </p>
      </div>
    );
  }
  return null;
};

const CustomLegend = ({ payload }) => {
  return (
    <div className="incident-severity-chart__legend">
      {payload.map((entry, index) => (
        <div key={index} className="incident-severity-chart__legend-item">
          <span
            className="incident-severity-chart__legend-dot"
            style={{ backgroundColor: entry.color }}
          />
          <span className="incident-severity-chart__legend-text">
            {entry.value}
          </span>
        </div>
      ))}
    </div>
  );
};

const mapStatus = (statusRaw) => {
  const status = (statusRaw || "").toString().toLowerCase();
  if (status.includes("progress")) return "inProgress";
  if (status.includes("open") || status.includes("new")) return "open";
  if (status.includes("active")) return "open";
  if (status.includes("resolved") || status.includes("closed")) return "closed";
  return "closed";
};

const mapSeverity = (sevRaw) => {
  const sev = (sevRaw || "").toString().toLowerCase();
  if (sev.includes("critical")) return "Critical";
  if (sev.includes("high")) return "High";
  if (sev.includes("medium")) return "Medium";
  if (sev.includes("low")) return "Low";
  return "Other";
};

export default function IncidentCountByStatusSeverity({
  range = "24h",
  clientId = null,
  incidents = null,
  loadingExternal = false,
  errorExternal = null,
}) {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const buildData = (rows) => {
    const bucket = {};
    rows.forEach((r) => {
      const sev = mapSeverity(r.severity || r.Severity);
      const stat = mapStatus(r.status || r.Status);
      if (!bucket[sev]) bucket[sev] = { severity: sev, open: 0, inProgress: 0, closed: 0 };
      bucket[sev][stat] = (bucket[sev][stat] || 0) + 1;
    });
    return Object.values(bucket).sort(
      (a, b) =>
        ["Critical", "High", "Medium", "Low", "Other"].indexOf(a.severity) -
        ["Critical", "High", "Medium", "Low", "Other"].indexOf(b.severity)
    );
  };

  useEffect(() => {
    let mounted = true;
    const run = async () => {
      // If incidents provided from parent, use them
      if (Array.isArray(incidents)) {
        setLoading(false);
        setError(errorExternal);
        setData(buildData(incidents));
        return;
      }
      try {
        setLoading(true);
        setError(null);
        const params = {};
        if (range) params.range = range;
        if (range.endsWith("d")) {
          const days = parseInt(range.replace("d", ""), 10);
          if (!Number.isNaN(days)) params.days = days;
        }
        const { data } = await apiClient.get(API_CONFIG.ENDPOINTS.INCIDENTS, { params });
        const rows = Array.isArray(data?.data) ? data.data : Array.isArray(data) ? data : [];
        if (!mounted) return;
        setData(buildData(rows));
      } catch (err) {
        if (mounted) {
          setError(err.response?.data?.message || err.message || "Failed to load incidents");
          setData([]);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };
    run();
    return () => {
      mounted = false;
    };
  }, [range, clientId, incidents, errorExternal]);

  return (
    <Box className="incident-severity-chart">
      <Box className="incident-severity-chart__header">
        <Typography variant="h5" className="dashboard__widget-title">
          Incident Count by Status and Severity
        </Typography>
        <Typography variant="body2" className="dashboard__widget-subtitle">
          Distribution of incidents by severity and their status
        </Typography>
      </Box>
      <Box className="incident-severity-chart__content">
        {(loading || loadingExternal) && <div className="text-muted">Loading incidents…</div>}
        {(error || errorExternal) && <div className="text-muted">Error: {error || errorExternal}</div>}
        {!loading && !error && data.length === 0 && (
          <div className="text-muted">No incident data for selected filters.</div>
        )}
        <ResponsiveContainer width="100%" height={320}>
          <BarChart
            data={data}
            margin={{ top: 20, right: 20, left: 0, bottom: 5 }}
            barCategoryGap="25%"
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="rgba(255, 255, 255, 0.08)"
              vertical={false}
            />
            <XAxis
              dataKey="severity"
              tick={{ fill: "#94a3b8", fontSize: 12, fontFamily: "Inter" }}
              axisLine={{ stroke: "rgba(255, 255, 255, 0.1)" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#94a3b8", fontSize: 12, fontFamily: "Inter" }}
              axisLine={{ stroke: "rgba(255, 255, 255, 0.1)" }}
              tickLine={false}
              domain={[0, "auto"]}
            />
            <Tooltip content={<CustomTooltip />} cursor={{ fill: "rgba(255, 255, 255, 0.05)" }} />
            <Legend content={<CustomLegend />} />
            <Bar
              dataKey="open"
              name="Open"
              stackId="a"
              fill={COLORS.open}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="inProgress"
              name="In progress"
              stackId="a"
              fill={COLORS.inProgress}
              radius={[0, 0, 0, 0]}
            />
            <Bar
              dataKey="closed"
              name="Closed"
              stackId="a"
              fill={COLORS.closed}
              radius={[4, 4, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </Box>
    </Box>
  );
}

